import React, { useCallback, useMemo, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import {
  Button,
  FormButton,
  Alert,
  PageHeader,
  ReadMode,
  Title,
} from '@cochlear-design-system/foundation';
import { getLabelsForFields } from '../../utils/labelHelper';
import { routes } from '../../config';
import AssignLoanerConfirmationModal from './components/AssignLoanerConfirmationModal';
import providerService from '../../services/providerService';
import { useAssignLoaner } from '../../hooks/useAssignLoaner';
import { logger } from '../../logger';

const useLabels = (rendering) =>
  useMemo(
    () => getLabelsForFields(rendering.fields),
    [rendering.fields],
  );

const recipientFields = [
  { key: 'name', getValue: (p) => p?.name?.item },
  { key: 'phone', getValue: (p) => p?.phoneNumber },
  { key: 'dob', getValue: (p) => p?.dob },
  { key: 'email', getValue: (p) => p?.email },
  { key: 'address', getValue: (p) => p?.address },
];

const loanerFields = [
  { key: 'deviceType', getValue: (l) => l?.product?.type },
  { key: 'description', getValue: (l) => l?.product?.description },
  { key: 'productFamily', getValue: (l) => l?.product?.family },
  {
    key: 'serialNumber',
    getValue: (l) => l?.product?.serialNumber,
  },
  {
    key: 'currentlyWith',
    getValue: (l, pn) => l?.currentlyWith || pn,
  },
];

const DetailsSection = ({
  titleLabel,
  fields,
  labelPrefix,
  labels,
}) => (
  <>
    {titleLabel && (
      <Title content={titleLabel} size="heading-3" tag="h3" />
    )}
    <section className="assign-loaner-modal__section">
      {fields.map(({ key, value }) => (
        <ReadMode
          key={key}
          details={[value]}
          title={labels[`${labelPrefix}.${key}`]}
        />
      ))}
    </section>
  </>
);

const AssignLoanerDetails = ({ rendering, country, lang }) => {
  const labels = useLabels(rendering);
  const log = logger();
  const history = useHistory();
  const location = useLocation();
  const { selectedLoaner, selectedPatient } = location.state || {};
  const [
    assignLoanerConfirmationModalVisible,
    setAssignLoanerConfirmationModalVisible,
  ] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const currentProvider = providerService.getCurrentProvider();
  const providerName = currentProvider?.name;
  const assignLoanerMutation = useAssignLoaner();

  const onCancelAssignLoanerDetails = useCallback(() => {
    history.push({
      pathname: `/${country}/${lang}/${routes.dpx.assignLoaner}`,
      state: { selectedLoaner },
    });
  }, [country, lang, history, selectedLoaner]);

  const handleAssignLoanerConfirm = () => {
    setHasError(false);
    setAssignLoanerConfirmationModalVisible(true);
  };

  const handleAssignLoanerConfirmationClose = useCallback(() => {
    setAssignLoanerConfirmationModalVisible(false);
    setHasError(false);
  }, []);

  const handleAssignLoanerConfirmationSubmit =
    useCallback(async () => {
      const assetId = selectedLoaner?.id;
      const patientId = selectedPatient?.cochlearId;

      if (currentProvider?.id && assetId && patientId) {
        setHasError(false);
        setIsSubmitting(true);

        try {
          const response = await assignLoanerMutation.mutateAsync({
            assetId,
            patientId,
          });

          if (response?.success) {
            history.push({
              pathname: `/${country}/${lang}/${routes.dpx.clinicEquipment}`,
              state: {
                assignLoanerSuccess: true,
                assignedAssetId: assetId,
              },
            });
          } else {
            setHasError(true);
          }
          setAssignLoanerConfirmationModalVisible(false);
        } catch (error) {
          log.error('Failed to assign loaner', error);
          setHasError(true);
        } finally {
          setIsSubmitting(false);
        }
      }
    }, [
      country,
      lang,
      history,
      currentProvider,
      selectedLoaner,
      selectedPatient,
      assignLoanerMutation,
    ]);

  const loanerDetailsData = loanerFields.map(({ key, getValue }) => ({
    key,
    value: getValue(selectedLoaner, providerName),
  }));

  return (
    <>
      <div className="ccl__container">
        <div className="ccl__main-clinic-equipment ccl__main-assign-loaner">
          <Button
            text={labels['labels.common.back']}
            variant="tertiary"
            icon="chevron-left"
            onClick={onCancelAssignLoanerDetails}
            className="back-button"
            iconPosition="leading"
            data-analytics="assign_loaner_details_back_to_clinic_equipment"
          />

          <PageHeader
            className="ccl__main-clinic-equipment__title"
            title={{
              content:
                labels['labels.assignLoanerDetails.header.title'],
            }}
          />

          <div className="ccl__dpx_assign-loaner-details-wrapper">
            {hasError && (
              <Alert
                variant="error"
                heading={labels['labels.common.apiError.title']}
              >
                {labels['labels.common.apiError.message']}
              </Alert>
            )}

            {/* Recipient Details */}
            <Title
              content={
                labels[
                  'labels.assignLoanerDetails.recipientDetails.title'
                ]
              }
              size="heading-3"
              tag="h3"
            />
            <DetailsSection
              titleLabel={null}
              fields={recipientFields.map(({ key, getValue }) => ({
                key,
                value: getValue(selectedPatient),
              }))}
              labelPrefix="labels.assignLoanerDetails.recipientDetails"
              labels={labels}
            />

            {/* Loaner Details */}
            <DetailsSection
              titleLabel={
                labels[
                  'labels.assignLoanerDetails.loanerDetails.title'
                ]
              }
              fields={loanerDetailsData}
              labelPrefix="labels.assignLoanerDetails.loanerDetails"
              labels={labels}
            />
          </div>

          <div className="ccl-e-form-button-bar mt-6">
            <div className="ccl-e-form-button-bar__save">
              <FormButton
                type="save"
                text={labels['labels.common.confirm']}
                onClick={handleAssignLoanerConfirm}
                data-analytics="submit_assign_loaner_details"
              />
            </div>
            <div className="ccl-e-form-button-bar__cancel">
              <Button
                text={labels['labels.common.cancel']}
                variant="secondary"
                onClick={onCancelAssignLoanerDetails}
                data-analytics="cancel_assign_loaner_details"
              />
            </div>
          </div>
        </div>
      </div>

      <AssignLoanerConfirmationModal
        labels={labels}
        onClose={handleAssignLoanerConfirmationClose}
        onSubmit={handleAssignLoanerConfirmationSubmit}
        visible={assignLoanerConfirmationModalVisible}
        isSubmitting={isSubmitting}
      />
    </>
  );
};

export default AssignLoanerDetails;
